import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// Typed event catalog
// ---------------------------------------------------------------------------

type EventProperties = {
  // Auth
  user_signed_up: {
    provider: string;
    referrer: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
  user_signed_in: { provider: string };
  user_signed_out: Record<string, never>;

  // Publishing
  page_published: { page_id: string };
  page_unpublished: { page_id: string };

  // Custom domains
  custom_domain_added: { domain: string };
  custom_domain_verified: { domain: string };
  custom_domain_removed: { domain: string };

  // Subscription
  checkout_started: Record<string, never>;
  checkout_failed: { source: 'checkout' | 'portal' };
  portal_opened: Record<string, never>;

  // Pages (dashboard)
  page_created: { source: 'dashboard' | 'template' };
  page_deleted: { page_id: string };
  page_duplicated: { page_id: string };

  // Account
  account_deleted: Record<string, never>;
  profile_updated: Record<string, never>;
  avatar_updated: Record<string, never>;
  username_changed: Record<string, never>;

  // Editor
  feedback_submitted: { category: string };
  autosave_failed: Record<string, never>;

  // Templates
  template_previewed: { template_id: string; category: string; source: 'landing' };
  template_selected: { template_id: string; category: string };
  template_demo_viewed: { template_id: string; category: string };
  template_saved: { template_id: string };
  custom_template_deleted: { template_id: string };

  // Errors
  error_boundary_triggered: { message: string };

  // Consent
  'consent:accepted': Record<string, never>;
  'consent:rejected': Record<string, never>;

  // Top-of-funnel
  landing_page_viewed: Record<string, never>;
  cta_clicked: { location: 'hero' | 'footer' | 'features_section' };
  signup_form_opened: Record<string, never>;
};

type EventName = keyof EventProperties;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function trackEvent<T extends EventName>(
  name: T,
  properties?: EventProperties[T],
): void {
  posthog.capture(name, properties);
}

export function trackPageView(): void {
  posthog.capture('$pageview');
}

export function setUserProperties(properties: Record<string, unknown>): void {
  posthog.setPersonProperties(properties);
}

/** Call on sign-in so future events are linked to the user. */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  posthog.identify(userId, traits);
}

/** Call on sign-out to disassociate subsequent events from the user. */
export function resetAnalyticsUser(): void {
  posthog.reset();
}
